import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import classnames from 'classnames';
import { pick } from 'lodash';
import { Button, Drawer, Notification, Radio, Spinner } from '@bringg/react-components';
import { Trans } from 'react-i18next';
import { observer } from 'mobx-react';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Portal } from '@bringg-frontend/bringg-web-infra';
import { PrivilegeTypes } from '@bringg/types';

import { ServiceAreaMap } from 'bringg-web/features/service-area/map';
import { useStores } from 'bringg-web/recipes';
import { AddServiceAreaButton } from 'bringg-web/features/service-area/add-button';
import { ServiceAreaForm } from 'bringg-web/features/service-area/form/form';
import { ServiceAreaTable } from './table';
import { ServiceAreaFilterBar } from 'bringg-web/features/service-area/form/filter-bar';
import { enclosePolygon } from 'bringg-web/features/service-area/utils/enclose-polygon';
import ServiceAreaUploadModal from 'bringg-web/features/service-area/upload-modal';
import { useHasFeatureFlag } from 'bringg-web/utils/feature-flags';
import { SERVICE_AREA_LAT_LNG_FF } from 'bringg-web/consts';
import { SERVICE_AREAS_DELETE_POLYGON_POINT_FF } from 'bringg-web/features/service-area/constants';
import ResourceAsyncOperationStatus from 'bringg-web/features/resources/resource-async-operation-status';
import { DecoratedServiceArea } from 'bringg-web/stores/service-areas-view/service-areas-view-store';
import { translationKeys, useServiceAreaTranslation } from './translations';
import { formatCompact } from './utils/format-compact';
import { useHasAccess } from 'bringg-web/utils/privileges';

import './service-area.scss';

export enum VIEW_MODE {
	MAP = 'map',
	TABLE = 'table'
}

export const ServiceArea = observer(({ teamId = null, readonly = false }: { teamId?: number; readonly?: boolean }) => {
	const { search } = useLocation();
	const { path } = useRouteMatch();
	const history = useHistory();
	const serviceAreaId = useMemo(() => new URLSearchParams(search).get('id'), [search]);

	const translations = useServiceAreaTranslation();
	const { serviceAreasStore, serviceAreaViewStore, merchantStore, teamsStore } = useStores();
	const [view, setView] = useState<VIEW_MODE>(VIEW_MODE.MAP);
	const [resourceId, setResourceId] = useState<string>(null);
	const [fileUploadOpen, setFileUploadOpen] = useState(false);
	const latLngFF = useHasFeatureFlag(SERVICE_AREA_LAT_LNG_FF);
	const deletePolygonPointEnabled = useHasFeatureFlag(SERVICE_AREAS_DELETE_POLYGON_POINT_FF);
	const newDispatcherSideMenuItemsFF = useHasFeatureFlag('dispatcher_new_side_menu_items');
	const canEditServiceArea = newDispatcherSideMenuItemsFF
		? useHasAccess(PrivilegeTypes.ALLOW_DISPATCHER_EDIT_SERVICE_AREA)
		: !readonly;

	const focusedServiceAreaId = serviceAreaViewStore.focusedServiceArea?.id;
	// the map makes the focused area's polygon editable only once the area is saved (it has an id)
	const showEditPolygonHint =
		deletePolygonPointEnabled &&
		canEditServiceArea &&
		Boolean(focusedServiceAreaId && serviceAreaViewStore.focusedServiceArea.polygon?.length);

	const createEmptyServiceArea = useCallback(() => ({ team_ids: teamId ? [teamId] : [] }), [teamId]);
	const urlFocusAppliedRef = useRef<string | null>(null);

	useEffect(() => {
		void serviceAreaViewStore.init(serviceAreasStore, latLngFF);
		void merchantStore.fetch();
		void teamsStore.fetchAll();
	}, [teamsStore, serviceAreasStore, merchantStore, serviceAreaViewStore, latLngFF]);

	const dataInitialized = serviceAreaViewStore.initialized && serviceAreasStore.allFetched;

	// Lightweight stats from raw store data (no LatLng creation) to gate large datasets
	const LARGE_DATA_THRESHOLD = 500_000;
	const PROCEED_DELAY_MS = 150;

	const [userConfirmedLargeData, setUserConfirmedLargeData] = useState(false);
	const [proceedLoading, setProceedLoading] = useState(false);

	useEffect(() => {
		if (!proceedLoading) return;
		const timer = setTimeout(() => setUserConfirmedLargeData(true), PROCEED_DELAY_MS);
		return () => clearTimeout(timer);
	}, [proceedLoading]);

	const dataStats = useMemo(() => {
		if (!dataInitialized) return null;
		let totalPoints = 0;
		let saCount = 0;
		const teamIdSet = new Set<number>();
		for (const sa of serviceAreasStore.getAll) {
			if (sa.polygon?.length) {
				saCount++;
				totalPoints += sa.polygon.length;
			}
			(sa.team_ids || []).forEach(id => teamIdSet.add(id));
		}
		return { saCount, totalPoints, teamCount: teamIdSet.size };
	}, [dataInitialized, serviceAreasStore]);

	const hasTeamFilter = !!teamId || (serviceAreaViewStore.filters?.byTeams?.length ?? 0) > 0;
	const isEditing = Boolean(serviceAreaViewStore.focusedServiceArea);
	const showLargeDataWarning =
		dataInitialized &&
		!hasTeamFilter &&
		!userConfirmedLargeData &&
		!isEditing &&
		dataStats?.totalPoints > LARGE_DATA_THRESHOLD;

	useEffect(() => {
		if (!dataInitialized || !serviceAreaId || showLargeDataWarning) {
			return;
		}

		if (urlFocusAppliedRef.current === serviceAreaId) {
			return;
		}
		urlFocusAppliedRef.current = serviceAreaId;

		if (serviceAreaId === 'new') {
			if (!serviceAreaViewStore.focusedServiceArea) {
				serviceAreaViewStore.focusServiceArea(createEmptyServiceArea());
			}
			return;
		}

		const serviceArea = serviceAreaViewStore.decorated.find(sa => sa.id === Number(serviceAreaId));

		if (serviceArea) {
			serviceAreaViewStore.focusServiceArea(serviceArea);
		}
	}, [dataInitialized, serviceAreaId, showLargeDataWarning]);
	useEffect(() => {
		if (!dataInitialized) {
			return;
		}
		const focused = serviceAreaViewStore.focusedServiceArea;
		const id = focused?.id || 'new';
		history.replace(!focused ? path : `${path}?id=${id}`);
	}, [serviceAreaViewStore.focusedServiceArea, dataInitialized, history, path]);

	const exitFromEditMode = useCallback(() => {
		serviceAreaViewStore.focusServiceArea(null);
	}, [serviceAreaViewStore]);

	const onSubmit = async (serviceArea, teamsServiceArea) => {
		try {
			await serviceAreaViewStore.save(serviceArea, teamsServiceArea, { teamId });
			serviceAreaViewStore.focusServiceArea(null);
		} catch {
			return Notification.error(translations.failedToCreate);
		}

		Notification.success(serviceArea.id ? translations.successfullyUpdated : translations.successfullyCreated);

		exitFromEditMode();
	};

	useEffect(() => {
		serviceAreaViewStore.filterBy({ byTeams: teamId ? [teamId] : null });
	}, [teamId, serviceAreaViewStore]);

	const onPolygonComplete = (serviceArea: Partial<DecoratedServiceArea>, googlePolygon: google.maps.Polygon) => {
		const polygon = enclosePolygon(
			googlePolygon
				.getPath()
				.getArray()
				.map(obj => (latLngFF ? [obj.lat(), obj.lng()] : [obj.lng(), obj.lat()]))
		);

		serviceAreaViewStore.focusServiceArea({ ...serviceArea, polygon });
		googlePolygon.setMap(null);
	};

	const onPolygonCreate = useCallback(
		(googlePolygon: google.maps.Polygon) => {
			const serviceArea = serviceAreaViewStore.focusedServiceArea
				? serviceAreaViewStore.focusedServiceArea
				: createEmptyServiceArea();

			onPolygonComplete(serviceArea, googlePolygon);
		},
		[serviceAreaViewStore.focusedServiceArea, createEmptyServiceArea]
	);

	const onPolygonEdit = useCallback(
		(googlePolygon: google.maps.Polygon) => {
			// Extract current path from the Google polygon and update the store.
			// Unlike onPolygonCreate, do NOT call setMap(null) -- the polygon must
			// stay on the map for continued editing.
			const polygon = enclosePolygon(
				googlePolygon
					.getPath()
					.getArray()
					.map(obj => (latLngFF ? [obj.lat(), obj.lng()] : [obj.lng(), obj.lat()]))
			);
			serviceAreaViewStore.focusServiceArea({
				...serviceAreaViewStore.focusedServiceArea,
				polygon
			});
		},
		[serviceAreaViewStore, latLngFF]
	);

	const onEdit = useCallback(
		serviceArea => {
			serviceAreaViewStore.focusServiceArea(serviceArea);
			setView(VIEW_MODE.MAP);
		},
		[serviceAreaViewStore]
	);

	const mapCenter = useMemo(() => {
		if (teamId) {
			return teamsStore.teams.get(teamId)?.lat ? pick(teamsStore.teams.get(teamId), ['lat', 'lng']) : null;
		}

		return merchantStore.merchant?.lat ? pick(merchantStore.merchant, ['lat', 'lng']) : null;
	}, [merchantStore.merchant, teamsStore.teams, teamId]);

	const onAdd = useCallback(() => {
		serviceAreaViewStore.focusServiceArea(createEmptyServiceArea());
		setView(VIEW_MODE.MAP);
	}, [serviceAreaViewStore, createEmptyServiceArea]);

	const onUploaded = useCallback(() => {
		void serviceAreasStore.forceFetchAll();
	}, [serviceAreasStore]);

	const onUploadClosed = useCallback(() => {
		setResourceId(null);
	}, []);

	return (
		<div
			className={classnames('service-area-page', {
				'team-page-offset': serviceAreaId && (newDispatcherSideMenuItemsFF || teamId)
			})}
		>
			<Drawer
				open={Boolean(serviceAreaId)}
				width="calc(var(--merchant-navigation-container-width) + var(--navbar-left-side-width))"
				mask={false}
				closable={false}
				placement="left"
			>
				<ServiceAreaForm showTeamsSection={!teamId} onSubmit={onSubmit} onClose={exitFromEditMode} />
			</Drawer>

			<div className="header">
				<h2 className="title">{translations.title}</h2>
				{canEditServiceArea ? (
					<AddServiceAreaButton
						showBulkUpload={!teamId}
						onBulkUpload={() => setFileUploadOpen(true)}
						onAdd={onAdd}
					/>
				) : null}
			</div>
			<div className="main">
				{!dataInitialized ? (
					<div className="service-area-map" data-test-id="service-area-map-container">
						<div className="service-area-map-loader">
							<Spinner size="large" />
						</div>
					</div>
				) : (
					<>
						<ServiceAreaFilterBar teamId={teamId} />
						<div className="map-tools-description">
							{view === VIEW_MODE.MAP && (
								<div>
									{showEditPolygonHint ? (
										<div data-test-id="edit-polygon-hint">{translations.editPolygonHint}</div>
									) : (
										translations.mapToolsDescription
									)}
								</div>
							)}
							{view === VIEW_MODE.TABLE && <div />}
							<Radio.Group buttonStyle="solid" value={view} onChange={e => setView(e.target.value)}>
								<Radio.Button value={VIEW_MODE.MAP}>
									<BringgIcon iconName={BringgFontIcons.Map} />
								</Radio.Button>
								<Radio.Button value={VIEW_MODE.TABLE}>
									<BringgIcon iconName={BringgFontIcons.Monitor} />
								</Radio.Button>
							</Radio.Group>
						</div>
						{view === VIEW_MODE.MAP ? (
							showLargeDataWarning ? (
								<div className="service-area-map" data-test-id="service-area-map-container">
									<div className="service-area-map-loader">
										<div className="service-area-map-loader__content">
											<h3>{translations.largeDatasetTitle}</h3>
											<p>
												<Trans
													i18nKey={translationKeys.largeDatasetMessage}
													values={{
														saCount: formatCompact(dataStats.saCount),
														teamCount: formatCompact(dataStats.teamCount),
														totalPoints: formatCompact(dataStats.totalPoints)
													}}
													components={{
														1: <strong />,
														2: <strong />,
														3: <strong />,
														4: <strong />
													}}
												/>
											</p>
											<p className="service-area-map-loader__impact">
												{translations.largeDatasetImpact}
											</p>
											<Button
												type="default"
												loading={proceedLoading}
												onClick={() => setProceedLoading(true)}
											>
												{translations.largeDatasetProceed}
											</Button>
										</div>
									</div>
								</div>
							) : (
								<div className="service-area-map" data-test-id="service-area-map-container">
									<ServiceAreaMap
										teamId={teamId}
										mapCenter={mapCenter}
										readonly={!canEditServiceArea}
										onPolygonCreate={canEditServiceArea ? onPolygonCreate : undefined}
										onPolygonEdit={canEditServiceArea ? onPolygonEdit : undefined}
									/>
								</div>
							)
						) : (
							<div data-test-id="service-area-table-container">
								<ServiceAreaTable readonly={!canEditServiceArea} onEdit={onEdit} teamId={teamId} />
							</div>
						)}
					</>
				)}
			</div>

			<ServiceAreaUploadModal
				open={fileUploadOpen}
				onUploadStarted={result => {
					setFileUploadOpen(false);
					setResourceId(result.resource_id);
				}}
				onCancel={() => setFileUploadOpen(false)}
			/>
			<ResourceAsyncOperationStatus
				minimized
				title={translations.progressTitle}
				translations={translations}
				onUploadFinished={onUploaded}
				resourceId={resourceId}
				onClose={onUploadClosed}
				errorsFileRender={status =>
					status.errors_url && (
						<a href={status.errors_url} target="_blank" rel="noreferrer">
							{translations.downloadErrorsFile}
						</a>
					)
				}
			/>
		</div>
	);
});

const ServiceAreaPortal = () => <Portal element={<ServiceArea />} nodeId="service-area-portal" />;

export default ServiceAreaPortal;
